import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import Sidebar from "../../components/layout/Sidebar";
import Header from "../../components/layout/Header";
import {
  Search,
  Filter,
  PlusSquare,
  Edit,
  Trash2,
  StickyNoteIcon,
  Link as LinkIcon,
  Layers,
  XCircle,
  CheckCircle,
  Banknote,
  Landmark,
} from "lucide-react";
import Pagination from "../../components/Pagination";
import { usePet } from "../../hooks/usePet";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import PetForm from "./PetForm";
import { toast } from "react-toastify";
import { useResolvedPath } from "react-router-dom";
import Skeleton from "../../components/ui/Skeleton";
import DeleteModal from "../../components/ui/DeleteModal";
import { useFileUpload } from "../../hooks/useFileUplode";
import ConfirmationModal from "../../components/ui/ConfirmationModal";

function Pet() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPet, setSelectedPet] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("view"); // view, edit, create
  const [contentsList, setContentsList] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [exclusiveDescription, setExclusiveDescription] = useState("");
  const [petData, setPetData] = useState({
    currentPet: 1,
    totalPets: 1,
    showing: 10,
    total: 0,
  });

  const queryClient = useQueryClient();

  const {
    fetchPet,
    createPet,
    updatePet,
    deletePet,
    togglePetStatus,
    toggleDefaultPet,
    toggleIsExclusive,
  } = usePet({
    pagination: { page: petData.currentPet, limit: 10 },
  });
  const { fileUpload, fileDelete } = useFileUpload();

  const contentsQuery = useQuery({
    queryKey: ["pet", petData.currentPet],
    queryFn: () =>
      fetchPet({
        pagination: { pet: petData.currentPet, limit: petData.showing },
      }),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (!contentsQuery.isLoading && contentsQuery.data) {
      const response = contentsQuery.data;
      setContentsList(response.data.pet || []);
      setPetData({
        currentPet: response.data.pagination?.currentPet || 1,
        totalPets: response.data.pagination?.lastPet || 1,
        total: response.data.pagination?.total || 0,
        showing: response.data.pagination?.showing || 10,
      });
    }
  }, [contentsQuery.data, contentsQuery.isLoading]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const openModal = (pet, mode) => {
    setSelectedPet(pet);
    setModalMode(mode);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedPet(null);
  };

  const handlePetChange = (pet) => {
    setPetData((prev) => ({
      ...prev,
      currentPet: pet,
    }));
  };

  const filteredPets = contentsList.filter((pet) => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        (pet.title && pet.title.toLowerCase().includes(query)) ||
        (pet.pet && pet.pet.toLowerCase().includes(query))
      );
    }
    return true;
  });

  const handleUpdate = (pet) => {
    openModal(pet, "edit");
  };

  const handleDelete = {
    onDelete: (pet) =>
      mutation.deletePet.mutate(pet._id, {
        onSuccess: (data) => {
          toast.success(data.message || "Pet Deleted");
          setDeleteModal(false);
        },
        onError: (error) => {
          toast.error(error.message || "Failed to delete pet");
        },
      }),
    onClose: () => setDeleteModal(false),
    onOpen: (pet) => {
      setDeleteModal(true);
      setSelectedPet(pet);
    },
  };

  const toggleStatus = (pet) => {
    mutation.toggleStatus.mutate(pet._id, {
      onSuccess: (data) => {
        toast.success("Status updated successfully");
      },
      onError: (error) => toast.error("Failed to update status"),
    });
  };

  const toggleDefault = (pet) => {
    mutation.toggleDefault.mutate(pet._id, {
      onSuccess: (data) => {
        toast.success("Default status updated successfully");
      },
      onError: (error) => toast.error("Failed to update default status"),
    });
  };

  const toggleExclusive = (pet, allowExclusive) => {
    mutation.toggleExclusive.mutate(
      { id: pet._id, allowExclusive },
      {
        onSuccess: (data) => {
          if (data.status === 405 && data?.data?.open_confirmation_modal) {
            setExclusiveDescription(data.message);
            setConfirmationModal(true);
          } else {
            setConfirmationModal(false);
            toast.success(data.message);
          }
        },
        onError: (error) => {
          toast.error(error.message);
        },
      }
    );
  };

  const mutation = {
    createPet: useMutation({
      mutationFn: (body) => {
        if (modalMode === "edit") {
          return updatePet(body);
        }
        return createPet(body);
      },
    }),
    deletePet: useMutation({
      mutationFn: (id) => deletePet(id),
    }),
    toggleStatus: useMutation({
      mutationFn: async (id) => await togglePetStatus(id),
    }),
    toggleDefault: useMutation({
      mutationFn: async (id) => await toggleDefaultPet(id),
    }),
    toggleExclusive: useMutation({
      mutationFn: async (data) =>
        await toggleIsExclusive(data.id, data.allowExclusive),
    }),
  };

  const handleSubmit = (body) => {
    if (modalMode === "edit") {
      body._id = selectedPet._id;
    }

    mutation.createPet.mutate(body, {
      onSuccess: async (data) => {
        await queryClient.invalidateQueries(["pet"]);
        toast.success(
          data?.data?.message ||
            `Pet ${modalMode === "edit" ? "Updated" : "Created"} successfully`
        );
        setShowModal(false);
      },
      onError: (error) => {
        toast.error(error.message || "Failed to save pet");
      },
    });
  };

  return (
    <div>
      <main className="">
        {/* Header and Add Button */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl font-bold">Pet</h1>
          <button
            onClick={() => openModal({}, "create")}
            className="flex items-center justify-center bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
          >
            <PlusSquare className="mr-2 h-4 w-4" />
            Add New Pet
          </button>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search pets..."
                value={searchQuery}
                onChange={handleSearch}
                className="pl-10 pr-4 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>

          {/* Table or Skeleton */}
          {contentsQuery.isLoading ? (
            <Skeleton.Card header rows={10} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Title
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Exclusive
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Price
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Points
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Image
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Status
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Default
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Created At
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPets.length > 0 ? (
                    filteredPets.map((pet) => (
                      <tr key={pet._id} className="hover:bg-gray-50 border-b">
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8 bg-gray-200 rounded-full flex items-center justify-center">
                              {pet.is_exclusive ? (
                                <Landmark className="h-4 w-4 text-purple-800" />
                              ) : (
                                <Banknote className="h-4 w-4 text-green-800" />
                              )}
                            </div>
                            <div className="ml-3">
                              <div
                                className={`font-medium ${
                                  pet.is_exclusive
                                    ? "text-purple-800"
                                    : "text-green-800"
                                }`}
                                title={
                                  pet.is_exclusive
                                    ? "Exclusive, available with only Pet Package"
                                    : "Normal, available with subscription or can be purchased using points or currency"
                                }
                              >
                                {pet.title}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {mutation.toggleExclusive.isPending ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              <svg
                                className="animate-spin h-4 w-4 text-gray-500"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                                ></path>
                              </svg>
                              <span className="ml-1">Updating</span>
                            </span>
                          ) : (
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer select-none ${
                                pet.is_exclusive
                                  ? "bg-purple-100 text-purple-800"
                                  : "bg-green-100 text-green-800"
                              }`}
                              onClick={() => {
                                setSelectedPet(pet);
                                toggleExclusive(pet);
                              }}
                              title="Toggle Exclusive"
                            >
                              {pet.is_exclusive ? (
                                <Landmark className="mr-1 h-3 w-3" />
                              ) : (
                                <Banknote className="mr-1 h-3 w-3" />
                              )}
                              {pet.is_exclusive ? "Exclusive" : "Normal"}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {pet.price}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {pet.points}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {pet.image ? (
                            <img
                              src={pet.image}
                              alt={pet.title || "Image"}
                              className="h-10 w-10 object-cover rounded"
                            />
                          ) : (
                            <div className="flex items-center">
                              <LinkIcon className="mr-1 h-4 w-4 text-gray-400" />
                              <span>No Image</span>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {mutation.toggleStatus.isPending ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              <svg
                                className="animate-spin h-4 w-4 text-gray-500"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                                ></path>
                              </svg>
                              <span className="ml-1">Updating</span>
                            </span>
                          ) : (
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer select-none ${
                                pet.is_active
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                              onClick={() => {
                                toggleStatus(pet);
                              }}
                              title="Toggle Status"
                            >
                              {pet.is_active ? (
                                <CheckCircle className="mr-1 h-3 w-3" />
                              ) : (
                                <XCircle className="mr-1 h-3 w-3" />
                              )}
                              {pet.is_active ? "Active" : "Inactive"}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {mutation.toggleDefault.isPending ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              <svg
                                className="animate-spin h-4 w-4 text-gray-500"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                                ></path>
                              </svg>
                              <span className="ml-1">Updating</span>
                            </span>
                          ) : (
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer select-none ${
                                pet.is_default
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                              onClick={() => {
                                toggleDefault(pet);
                              }}
                              title="Toggle Default"
                            >
                              {pet.is_default ? (
                                <CheckCircle className="mr-1 h-3 w-3" />
                              ) : (
                                <XCircle className="mr-1 h-3 w-3" />
                              )}
                              {pet.is_default ? "Default" : "Not Default"}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {moment(pet.created_at).format(
                            "MMMM Do YYYY, h:mm:ss a"
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => openModal(pet, "view")}
                              className="text-gray-400 hover:text-gray-500"
                              title="View Details"
                            >
                              <StickyNoteIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleUpdate(pet)}
                              className="text-blue-400 hover:text-blue-500"
                              title="Edit Pet"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              className="text-red-400 hover:text-red-500"
                              onClick={() => handleDelete.onOpen(pet)}
                              title="Delete Pet"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-8 text-center text-gray-500"
                      >
                        No pets found matching your search criteria
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <Pagination
            currentPage={petData.currentPet}
            onPageChange={handlePetChange}
            showing={petData.showing}
            totalPages={petData.totalPets}
            total={petData.total}
          />
        </div>
      </main>
      <DeleteModal
        isOpen={deleteModal}
        onClose={handleDelete.onClose}
        onDelete={() => handleDelete.onDelete(selectedPet)}
        title="Pet"
      />
      <ConfirmationModal
        isOpen={confirmationModal}
        onClose={() => setConfirmationModal(false)}
        onSuccess={() => toggleExclusive(selectedPet, true)}
        description={exclusiveDescription}
        title="Make pet exclusive"
      />
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={closeModal}
          ></div>
          <div className="h-4/5 relative bg-white rounded-2xl max-w-2xl w-full mx-4 shadow-xl overflow-auto">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-medium">
                {modalMode === "view"
                  ? "Pet Details"
                  : modalMode === "edit"
                  ? "Edit Pet"
                  : "Create Pet"}
              </h3>
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
                onClick={closeModal}
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <PetForm
                mode={modalMode}
                defaultValues={
                  selectedPet
                    ? {
                        ...selectedPet,
                        created_at: moment(selectedPet.created_at).format(
                          "YYYY-MM-DD"
                        ),
                        contentIds: selectedPet.contentIds || [],
                      }
                    : {}
                }
                onCancel={closeModal}
                contentsList={contentsList}
                onSubmit={(data) => handleSubmit(data)}
                submissionLoading={mutation.createPet.isLoading}
                fileUpload={fileUpload}
                fileDelete={fileDelete}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Pet;
